/**
 * PersonalInfoStep Component
 *
 * Second step: Collect personal information about the primary owner.
 *
 * STATE MANAGEMENT:
 * - Form state initialized from props (lazy initialization)
 * - Local state controls the form after mount
 * - Debounced auto-save (800ms) prevents excessive API calls
 * - No sync-from-props (industry standard for auto-save forms)
 * - Parent can reset via key={application.id} if needed
 */

import type { IMerchant, IOnboardingApplication, IOwner } from '@order-up/models';
import { useAuth, useGetUserByUid } from '@order-up/client/vite';
import { useEffect, useRef, useState } from 'react';
import { useDebouncedSave } from '../../lib/hooks';
import { US_STATES } from '../../types/onboarding';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';

export interface PersonalInfoStepProps {
  application: IOnboardingApplication;
}

interface FormData {
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string;
  ownerDob: string;
  ownerAddress: {
    address1: string;
    address2: string;
    city: string;
    stateCd: string;
    zip: string;
  };
}

/** Split a full name into first/last at the first space. */
function parseOwnerName(fullName: string): { first: string; last: string } {
  const trimmed = fullName.trim();
  const spaceIdx = trimmed.indexOf(' ');
  if (spaceIdx === -1) return { first: trimmed, last: '' };
  return { first: trimmed.slice(0, spaceIdx), last: trimmed.slice(spaceIdx + 1) };
}

/** Check if a date string represents someone at least 18 years old. */
function isAtLeast18(dateStr: string): boolean {
  if (!dateStr) return true; // don't flag empty — "required" handles that
  const dob = new Date(dateStr);
  if (isNaN(dob.getTime())) return true;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age >= 18;
}

// Helper to create form data from owner data with optional Firestore-user fallback.
// Signup persists `firstName` / `lastName` to the Firestore user doc (via
// `useSignUp` → `createUserProfile`). When the user reaches the onboarding
// `PersonalInfoStep` for the first time the form is empty, so we seed it from
// the user doc — same convention used today for `ownerEmail` (already pulled
// from `useAuth`). The user can still override either field; we only seed when
// the form is otherwise empty.
function createFormData(
  ownerData: Partial<IOwner> = {},
  authEmail?: string,
  firestoreFirstName?: string,
  firestoreLastName?: string,
): FormData {
  const { first: parsedFirst, last: parsedLast } = parseOwnerName(ownerData.ownerName || '');
  return {
    ownerFirstName: parsedFirst || firestoreFirstName || '',
    ownerLastName: parsedLast || firestoreLastName || '',
    ownerEmail: authEmail || ownerData.ownerEmail || '',
    ownerDob: ownerData.ownerDob || '',
    ownerAddress: {
      address1: ownerData.ownerAddress?.address1 || '',
      address2: ownerData.ownerAddress?.address2 || '',
      city: ownerData.ownerAddress?.city || '',
      stateCd: ownerData.ownerAddress?.stateCd || '',
      zip: ownerData.ownerAddress?.zip || ''
    }
  };
}

export function PersonalInfoStep({ application }: PersonalInfoStepProps) {
  const { data: authData } = useAuth();
  const authEmail = authData?.authUser?.email || '';

  // Pull the Firestore user doc so we can seed firstName/lastName from
  // signup — avoids re-asking what the user already entered moments ago
  // on the auth signup form. `useGetUserByUid` is cached + invalidated by
  // the same auth-state listener that drives `useAuth`.
  const { data: firestoreUser } = useGetUserByUid(authData?.authUser?.uid);

  const { debouncedSave } = useDebouncedSave({
    applicationId: application.id,
    currentStep: application.currentStep
  });

  const [formData, setFormData] = useState<FormData>(() =>
    createFormData(
      application.applicationData.ownership?.owner,
      authEmail,
      firestoreUser?.firstName,
      firestoreUser?.lastName,
    )
  );

  // The Firestore user doc may load asynchronously after the form
  // mounts (single Firestore round-trip). When that happens AFTER the
  // initial state has already settled with empty name fields, seed
  // them in-place. This is gated to "only seed when fields are still
  // empty" so we never clobber a value the user has just typed.
  const hasSeededFromFirestoreUser = useRef(false);
  useEffect(() => {
    if (hasSeededFromFirestoreUser.current) return;
    if (!firestoreUser) return;
    if (formData.ownerFirstName || formData.ownerLastName) {
      hasSeededFromFirestoreUser.current = true; // already populated; no-op
      return;
    }
    const next: FormData = {
      ...formData,
      ownerFirstName: firestoreUser.firstName ?? '',
      ownerLastName: firestoreUser.lastName ?? '',
    };
    // Async data-seeding from an external system (Firestore user doc).
    // The lint rule flags this on principle; the pattern here is correct
    // because we gate on `hasSeededFromFirestoreUser` so the effect runs
    // exactly once per Firestore-data arrival and never loops.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFormData(next);
    hasSeededFromFirestoreUser.current = true;

    // Persist the seeded values so future step revisits read them from
    // the application row (no second Firestore round-trip needed).
    const composedName = `${next.ownerFirstName} ${next.ownerLastName}`.trim();
    if (composedName) {
      const updates: Partial<IMerchant> = {
        ownership: {
          ...application.applicationData.ownership,
          owner: {
            ...application.applicationData.ownership?.owner,
            ownerName: composedName,
            ownerEmail: next.ownerEmail,
          },
        },
      };
      debouncedSave(updates);
    }
  }, [firestoreUser, formData, application.applicationData.ownership, debouncedSave]);

  // Compose first + last back to ownerName for the API
  const composeOwnerName = (first: string, last: string) =>
    `${first} ${last}`.trim();

  const handleChange = (field: string, rawValue: string) => {
    const updatedFormData = { ...formData };

    if (field.startsWith('ownerAddress.')) {
      const addressField = field.split('.')[1];
      updatedFormData.ownerAddress = {
        ...updatedFormData.ownerAddress,
        [addressField]: rawValue,
      };
    } else {
      switch (field) {
        case 'ownerFirstName':
          updatedFormData.ownerFirstName = rawValue;
          break;
        case 'ownerLastName':
          updatedFormData.ownerLastName = rawValue;
          break;
        case 'ownerDob':
          updatedFormData.ownerDob = rawValue;
          break;
      }
    }

    setFormData(updatedFormData);

    const updates: Partial<IMerchant> = {
      ownership: {
        ...application.applicationData.ownership,
        owner: {
          ...application.applicationData.ownership?.owner,
          ownerName: composeOwnerName(updatedFormData.ownerFirstName, updatedFormData.ownerLastName),
          ownerEmail: updatedFormData.ownerEmail,
          ownerDob: updatedFormData.ownerDob,
          ownerAddress: updatedFormData.ownerAddress,
        }
      }
    };

    debouncedSave(updates);
  };

  const dobError = formData.ownerDob && !isAtLeast18(formData.ownerDob)
    ? 'You must be at least 18 years old to apply.'
    : undefined;

  return (
    <div className="space-y-6">
      {/* First Name / Last Name */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="First Name"
          type="text"
          value={formData.ownerFirstName}
          onChange={(e) => handleChange('ownerFirstName', e.target.value)}
          placeholder="John"
          required
        />

        <Input
          label="Last Name"
          type="text"
          value={formData.ownerLastName}
          onChange={(e) => handleChange('ownerLastName', e.target.value)}
          placeholder="Doe"
          required
        />
      </div>

      {/* Owner Email — pre-populated from auth, not editable */}
      <Input
        label="Email Address"
        type="email"
        value={authEmail}
        disabled
        required
      />

      {/* Owner Date of Birth */}
      <Input
        label="Date of Birth"
        type="date"
        value={formData.ownerDob}
        onChange={(e) => handleChange('ownerDob', e.target.value)}
        required
        error={dobError}
      />

      {/* Address Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-white">Owner Address</h3>

        <Input
          label="Address"
          type="text"
          value={formData.ownerAddress.address1}
          onChange={(e) => handleChange('ownerAddress.address1', e.target.value)}
          placeholder="123 Main St"
          required
          />

        <Input
          label="Apt, Suite, Unit (optional)"
          type="text"
          value={formData.ownerAddress.address2}
          onChange={(e) => handleChange('ownerAddress.address2', e.target.value)}
          placeholder="Apt 4B"
          maxLength={200}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="City"
            type="text"
            value={formData.ownerAddress.city}
            onChange={(e) => handleChange('ownerAddress.city', e.target.value)}
            placeholder="San Francisco"
            required
              />

          <Select
            label="State"
            value={formData.ownerAddress.stateCd}
            onChange={(e) => handleChange('ownerAddress.stateCd', e.target.value)}
            required
              >
            <option value="">Select State</option>
            {US_STATES.map((state) => (
              <option key={state.value} value={state.value}>
                {state.label}
              </option>
            ))}
          </Select>
        </div>

        <Input
          label="ZIP Code"
          type="text"
          value={formData.ownerAddress.zip}
          onChange={(e) => handleChange('ownerAddress.zip', e.target.value)}
          placeholder="94102"
          required
            maxLength={10}
        />
      </div>

      {/* Info callout */}
      <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-4">
        <p className="text-sm text-blue-200/80">
          <span className="font-medium text-blue-300">Privacy Note:</span>
          {' '}Your personal information is encrypted and securely stored. We use this information
          solely for identity verification and compliance purposes as required by payment processors.
        </p>
      </div>
    </div>
  );
}
